import { type SelectProps } from 'antd';
import { debounce } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import { type UserPreviewDataFragment } from '../../../api/fragments/UserPreviewData';
import { useGetUsersOptionsLazyQuery } from '../../../api/queries/getUsersOptions';
import { type UserFilteringRule } from '../../../api/types';
import SearchUserWithTableField from '../../../components/fields/SearchUserWithTableField';

export type SearchUserWithTableFieldByRoleProps = {
    name: string;
    role: RoleSpecsFragment;
    displayName?: string;
    email?: string;
    debounceTimeout: number;
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.children.toString().toLowerCase().includes(inputValue.toLowerCase());

const SearchUserWithTableFieldByRole = ({
    name,
    role,
    email,
    displayName,
    debounceTimeout,
}: SearchUserWithTableFieldByRoleProps) => {
    const roleId = role.id;

    const filter = useMemo((): UserFilteringRule => ({ email, displayName }), [email, displayName]);
    const [userOptions, setUserOptions] = useState<UserPreviewDataFragment[]>([]);
    const [getUserOptions, { loading }] = useGetUsersOptionsLazyQuery({
        fetchPolicy: 'network-only',
    });

    const debounceFetcher = useMemo(() => {
        const loadUserOptions = async () => {
            const { data } = await getUserOptions({ variables: { roleId, filter } });
            const options = data?.users || [];
            setUserOptions(options);
        };

        return debounce(debounceTimeout, loadUserOptions);
    }, [debounceTimeout, filter, getUserOptions, roleId]);

    return (
        <SearchUserWithTableField
            filterOption={filterOption}
            loading={loading}
            mode="multiple"
            name={name}
            onSearch={debounceFetcher}
            style={{ width: '100%' }}
            users={userOptions}
        />
    );
};

export default SearchUserWithTableFieldByRole;
